import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
    Box, Typography, Button, TextField, InputAdornment, ToggleButton, ToggleButtonGroup,
    Tooltip, Paper, Chip, Card, CardContent, Divider,
    IconButton, useTheme, useMediaQuery, CircularProgress,
    FormControl, InputLabel, Select, MenuItem, Autocomplete,
} from '@mui/material';
import {
    Search, Add, Edit, Delete, Visibility,
} from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';

import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { DataTableWithStaticSchema } from 'components/DataTable';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import {
    SWIPE_ACTION_WIDTH_PX,
    SWIPE_DIRECTION_LOCK_PX,
    clampSwipeOffset,
    computeSnappedOffset,
} from 'utils/iosSwipeRow';
import { BusinessPlanDialog } from './BusinessPlanDialog';
import { BusinessPlanViewDialog } from './BusinessPlanViewDialog';

interface BusinessPlan {
    _id: string;
    planName: string;
    tagline: string;
    country: string;
    rootDomain: string;
    pricing: { monthlyPrice: number; annualMonthlyPrice: number };
    upgradeRoutes: { monthly: any[]; annual: any[] };
    downgradeRoutes: { monthly: any[]; annual: any[] };
    productPlans: any[];
    createdBy?: string;
    modifiedBy?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface EnvConfig {
    countries: { code: string; label: string; dialCode: string }[];
    currencies: { code: string; symbol: string; label: string }[];
    countryToCurrency: Record<string, string>;
    licenseTypes: string[];
    modules: string[];
    rootDomains: string[];
}

const VIEWMODE_STORAGE_KEY = 'business-plans-view-mode';
const TILE_PAGE_SIZE = 12;

const tableSchema = {
    paths: {
        _id: { instance: 'String', isRequired: false },
        planName: { instance: 'String', isRequired: true },
        country: { instance: 'String', isRequired: true },
        monthlyPrice: { instance: 'Number', isRequired: false },
        annualMonthlyPrice: { instance: 'Number', isRequired: false },
    },
    obj: {},
};

export const BusinessPlans: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
        const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
        return stored === 'table' ? 'table' : 'tile';
    });

    const [plans, setPlans] = useState<BusinessPlan[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);

    const [filterCountry, setFilterCountry] = useState<string>('');
    const [filterDomain, setFilterDomain] = useState<string>('');

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState<BusinessPlan | null>(null);

    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewingPlan, setViewingPlan] = useState<BusinessPlan | null>(null);

    const [envConfig, setEnvConfig] = useState<EnvConfig | null>(null);

    const [tileVisibleCount, setTileVisibleCount] = useState(TILE_PAGE_SIZE);

    const [tileSwipeRow, setTileSwipeRow] = useState<{
        tileId: string; offset: number; dragging: boolean;
    } | null>(null);
    const swipeSessionRef = useRef<{
        tileId: string; startX: number; startY: number; baseOffset: number; lock: 'h' | 'v' | null;
    } | null>(null);
    const latestSwipeOffsetRef = useRef(0);

    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

    const fetchEnvConfig = useCallback(async () => {
        try {
            const response = await apiClient.get('/businessplan/env/config');
            setEnvConfig(response.data);
        } catch (error) {
            Logger.error('BusinessPlans', `Failed to fetch env config: ${error}`);
        }
    }, []);

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/businessplan/all');
            setPlans(response.data || []);
        } catch (error) {
            Logger.error('BusinessPlans', `Failed to fetch plans: ${error}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEnvConfig();
        fetchPlans();
    }, [fetchEnvConfig, fetchPlans]);

    const countryLabelMap = useMemo(() => {
        if (!envConfig) return {} as Record<string, string>;
        return envConfig.countries.reduce((acc, c) => { acc[c.code] = c.label; return acc; }, {} as Record<string, string>);
    }, [envConfig]);

    const availableDomains = useMemo(() => {
        const domains = new Set<string>();
        plans.forEach(p => { if (p.rootDomain) domains.add(p.rootDomain); });
        return Array.from(domains).sort();
    }, [plans]);

    const currencyForCountry = useCallback((countryCode: string) => {
        return envConfig?.countryToCurrency?.[countryCode] || 'GBP';
    }, [envConfig]);

    const currencySymbol = useCallback((currencyCode: string) => {
        const curr = envConfig?.currencies?.find(c => c.code === currencyCode);
        return curr?.symbol || currencyCode;
    }, [envConfig]);

    const filteredPlans = useMemo(() => {
        let result = plans;

        if (filterCountry) {
            result = result.filter(p => p.country === filterCountry);
        }
        if (filterDomain) {
            result = result.filter(p => p.rootDomain === filterDomain);
        }

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(p =>
                p.planName.toLowerCase().includes(q) ||
                p.tagline?.toLowerCase().includes(q) ||
                p.country.toLowerCase().includes(q) ||
                (countryLabelMap[p.country] || '').toLowerCase().includes(q) ||
                (p.rootDomain || '').toLowerCase().includes(q)
            );
        }

        return result;
    }, [plans, searchQuery, countryLabelMap, filterCountry, filterDomain]);

    useEffect(() => {
        setTileVisibleCount(TILE_PAGE_SIZE);
    }, [searchQuery, filterCountry, filterDomain, plans]);

    const paginatedPlans = useMemo(() => filteredPlans.slice(0, tileVisibleCount), [filteredPlans, tileVisibleCount]);
    const hasMoreTiles = tileVisibleCount < filteredPlans.length;

    const handleCreate = () => {
        setEditingPlan(null);
        setDialogOpen(true);
    };

    const handleView = (plan: BusinessPlan) => {
        setViewingPlan(plan);
        setViewDialogOpen(true);
    };

    const handleEdit = (plan: BusinessPlan) => {
        setEditingPlan(plan);
        setDialogOpen(true);
    };

    const handleDelete = async (plan: BusinessPlan) => {
        if (!window.confirm(`Delete business plan "${plan.planName}" for country "${countryLabelMap[plan.country] || plan.country}"?`)) return;
        try {
            await apiClient.delete(`/businessplan/${plan._id}`);
            setRefreshKey(k => k + 1);
            fetchPlans();
        } catch (error) {
            Logger.error('BusinessPlans', `Failed to delete plan: ${error}`);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditingPlan(null);
    };

    const handleDialogSave = () => {
        setDialogOpen(false);
        setEditingPlan(null);
        setRefreshKey(k => k + 1);
        fetchPlans();
    };

    const formatPrice = (amount: number, countryCode: string) => {
        const cc = currencyForCountry(countryCode);
        const sym = currencySymbol(cc);
        const formatted = (amount / 100).toFixed(2);
        return `${sym}${formatted}`;
    };

    // --- Swipe handlers (iOS-style, same pattern as DataSets) ---
    const handleTileTouchStart = useCallback(
        (e: React.TouchEvent, plan: BusinessPlan) => {
            if (!isMobile) return;
            const t = e.touches[0];
            setTileSwipeRow((prev) => {
                const anotherWasOpen = Boolean(prev && prev.tileId !== plan._id && Math.abs(prev.offset) > 0.5);
                const baseOffset = !anotherWasOpen && prev?.tileId === plan._id ? prev.offset : 0;
                swipeSessionRef.current = {
                    tileId: plan._id, startX: t.clientX, startY: t.clientY, baseOffset, lock: null,
                };
                latestSwipeOffsetRef.current = baseOffset;
                return { tileId: plan._id, offset: baseOffset, dragging: true };
            });
        }, [isMobile]
    );

    const handleTileTouchMove = useCallback(
        (e: React.TouchEvent, plan: BusinessPlan) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.tileId !== plan._id) return;
            const touch = e.touches[0];
            const dx = touch.clientX - sess.startX;
            const dy = touch.clientY - sess.startY;

            if (sess.lock === null) {
                if (Math.abs(dx) > SWIPE_DIRECTION_LOCK_PX && Math.abs(dx) > Math.abs(dy) * 1.2) {
                    sess.lock = 'h';
                } else if (Math.abs(dy) > SWIPE_DIRECTION_LOCK_PX && Math.abs(dy) > Math.abs(dx) * 1.2) {
                    sess.lock = 'v';
                }
            }
            if (sess.lock !== 'h') return;

            const raw = sess.baseOffset + dx;
            const clamped = clampSwipeOffset(raw, true, true, SWIPE_ACTION_WIDTH_PX);
            latestSwipeOffsetRef.current = clamped;
            setTileSwipeRow({ tileId: plan._id, offset: clamped, dragging: true });
        }, [isMobile]
    );

    const handleTileTouchEnd = useCallback(
        (e: React.TouchEvent, plan: BusinessPlan) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.tileId !== plan._id) {
                swipeSessionRef.current = null;
                return;
            }
            const t = e.changedTouches[0];
            const dx = t.clientX - sess.startX;
            const dy = t.clientY - sess.startY;
            const { lock, baseOffset } = sess;
            swipeSessionRef.current = null;

            const finishSnap = (off: number) => {
                const snapped = computeSnappedOffset(off, true, true, SWIPE_ACTION_WIDTH_PX);
                latestSwipeOffsetRef.current = snapped;
                setTileSwipeRow({ tileId: plan._id, offset: snapped, dragging: false });
            };

            if (lock === null && Math.abs(dx) > Math.abs(dy) * 1.2 && Math.abs(dx) > 18) {
                const raw = baseOffset + dx;
                finishSnap(clampSwipeOffset(raw, true, true, SWIPE_ACTION_WIDTH_PX));
                return;
            }

            if (lock !== 'h') {
                if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
                    if (Math.abs(baseOffset) > 0.5) {
                        latestSwipeOffsetRef.current = 0;
                        setTileSwipeRow({ tileId: plan._id, offset: 0, dragging: false });
                    } else {
                        handleView(plan);
                    }
                } else {
                    latestSwipeOffsetRef.current = baseOffset;
                    setTileSwipeRow({ tileId: plan._id, offset: baseOffset, dragging: false });
                }
                return;
            }

            finishSnap(latestSwipeOffsetRef.current);
        }, [isMobile]
    );

    const handleTileTouchCancel = useCallback(
        (_e: React.TouchEvent, plan: BusinessPlan) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.tileId !== plan._id) return;
            swipeSessionRef.current = null;
            const snapped = computeSnappedOffset(latestSwipeOffsetRef.current, true, true, SWIPE_ACTION_WIDTH_PX);
            latestSwipeOffsetRef.current = snapped;
            setTileSwipeRow({ tileId: plan._id, offset: snapped, dragging: false });
        }, [isMobile]
    );

    // --- Table: customActions for View button (Edit & Delete come from onEdit/onDelete props) ---
    const customActions = useCallback((record: any) => {
        return [{
            label: 'View Details',
            onClick: () => handleView(record),
            color: 'info' as const,
            icon: <Visibility />,
        }];
    }, []);

    // --- Render tile view ---
    const renderTileView = () => (
        <Box display="flex" flexDirection="column" gap={2.5}>
            <Box
                display="flex"
                flexWrap="wrap"
                gap={2.5}
                sx={{ justifyContent: 'center' }}
            >
                {paginatedPlans.map((plan) => {
                    const cc = currencyForCountry(plan.country);
                    const rowOffset = isMobile && tileSwipeRow?.tileId === plan._id ? tileSwipeRow.offset : 0;
                    const rowDragging = isMobile && tileSwipeRow?.tileId === plan._id && tileSwipeRow.dragging;

                    if (!isMobile) {
                        return (
                            <Card
                                key={plan._id}
                                elevation={0}
                                onClick={() => handleView(plan)}
                                sx={{
                                    width: 280,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'box-shadow 0.2s',
                                    cursor: 'pointer',
                                    '&:hover': { boxShadow: theme.shadows[4] },
                                    '&:hover .bp-tile-actions': { opacity: 1 },
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                                            {plan.planName}
                                        </Typography>
                                        <Box display="flex" gap={0.5} flexShrink={0} ml={1}>
                                            <Chip label={plan.country} size="small" color="primary" sx={{ fontWeight: 600 }} />
                                            {plan.rootDomain && (
                                                <Chip label={plan.rootDomain} size="small" color="secondary" variant="outlined" sx={{ fontWeight: 600 }} />
                                            )}
                                        </Box>
                                    </Box>
                                    {plan.tagline && (
                                        <Typography variant="body2" color="text.secondary" mb={1.5}>{plan.tagline}</Typography>
                                    )}
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">Monthly</Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {formatPrice(plan.pricing.monthlyPrice, plan.country)}/mo
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography variant="caption" color="text.secondary">Annual</Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {formatPrice(plan.pricing.annualMonthlyPrice, plan.country)}/mo
                                        </Typography>
                                    </Box>
                                    <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                                        {plan.productPlans.map((pp, idx) => (
                                            <Chip
                                                key={idx}
                                                label={`${pp.module} (${pp.licenseType})`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem' }}
                                            />
                                        ))}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {countryLabelMap[plan.country] || plan.country} &middot; {cc}
                                    </Typography>
                                </CardContent>
                                <Box
                                    className="bp-tile-actions"
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: 0.5,
                                        px: 2,
                                        pb: 1.5,
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                    }}
                                >
                                    <Tooltip title="Edit">
                                        <Button color="secondary" variant="contained" size="small"
                                            onClick={(e) => { e.stopPropagation(); handleEdit(plan); }}
                                            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                            <Edit fontSize="small" />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <Button color="error" variant="contained" size="small"
                                            onClick={(e) => { e.stopPropagation(); handleDelete(plan); }}
                                            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                            <Delete fontSize="small" />
                                        </Button>
                                    </Tooltip>
                                </Box>
                            </Card>
                        );
                    }

                    // Mobile: iOS-style swipe tile
                    return (
                        <Box
                            key={plan._id}
                            sx={{
                                width: '100%',
                                maxWidth: 400,
                                borderRadius: 2,
                                overflow: 'hidden',
                                position: 'relative',
                                flexShrink: 0,
                                border: '1px solid',
                                borderColor: 'divider',
                                touchAction: 'pan-y',
                                isolation: 'isolate',
                            }}
                        >
                            {/* Swipe action strip behind the card */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'stretch',
                                    zIndex: 0,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: SWIPE_ACTION_WIDTH_PX,
                                        flexShrink: 0,
                                        bgcolor: 'secondary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Tooltip title="Edit">
                                        <IconButton
                                            aria-label="Edit plan"
                                            size="medium"
                                            sx={{ color: 'secondary.contrastText', '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' } }}
                                            onClick={(e) => { e.stopPropagation(); setTileSwipeRow(null); handleEdit(plan); }}
                                        >
                                            <Edit fontSize="medium" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }} />
                                <Box
                                    sx={{
                                        width: SWIPE_ACTION_WIDTH_PX,
                                        flexShrink: 0,
                                        bgcolor: 'error.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Tooltip title="Delete">
                                        <IconButton
                                            aria-label="Delete plan"
                                            size="medium"
                                            sx={{ color: 'error.contrastText', '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' } }}
                                            onClick={(e) => { e.stopPropagation(); setTileSwipeRow(null); handleDelete(plan); }}
                                        >
                                            <Delete fontSize="medium" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>

                            {/* Foreground card with swipe translate */}
                            <Box
                                onTouchStart={(e) => handleTileTouchStart(e, plan)}
                                onTouchMove={(e) => handleTileTouchMove(e, plan)}
                                onTouchEnd={(e) => handleTileTouchEnd(e, plan)}
                                onTouchCancel={(e) => handleTileTouchCancel(e, plan)}
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    backgroundColor: 'background.paper',
                                    transform: `translateX(${rowOffset}px)`,
                                    transition: rowDragging ? 'none' : 'transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                    willChange: rowDragging ? 'transform' : 'auto',
                                    boxShadow: rowOffset !== 0 ? 3 : 'none',
                                    p: 2,
                                }}
                            >
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                                        {plan.planName}
                                    </Typography>
                                    <Box display="flex" gap={0.5} flexShrink={0} ml={1}>
                                        <Chip label={plan.country} size="small" color="primary" sx={{ fontWeight: 600 }} />
                                        {plan.rootDomain && (
                                            <Chip label={plan.rootDomain} size="small" color="secondary" variant="outlined" sx={{ fontWeight: 600 }} />
                                        )}
                                    </Box>
                                </Box>
                                {plan.tagline && (
                                    <Typography variant="body2" color="text.secondary" mb={1}>{plan.tagline}</Typography>
                                )}
                                <Divider sx={{ mb: 1 }} />
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="caption" color="text.secondary">Monthly</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatPrice(plan.pricing.monthlyPrice, plan.country)}/mo
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">Annual</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatPrice(plan.pricing.annualMonthlyPrice, plan.country)}/mo
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {filteredPlans.length === 0 && !loading && (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                    <Typography color="text.secondary">
                        {searchQuery || filterCountry || filterDomain
                            ? 'No business plans match your filters.'
                            : 'No business plans found. Create one to get started.'}
                    </Typography>
                </Paper>
            )}

            {filteredPlans.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {paginatedPlans.length} of {filteredPlans.length} plan{filteredPlans.length !== 1 ? 's' : ''}
                    </Typography>
                    {hasMoreTiles && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setTileVisibleCount(prev => prev + TILE_PAGE_SIZE)}
                        >
                            Load More
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );

    // --- Render table view ---
    const renderTableView = () => (
        <DataTableWithStaticSchema
            dataUrl="/businessplan/all"
            schemaObject={tableSchema}
            columnsToExclude={['_id']}
            columnsWithDisabledFilter={['monthlyPrice', 'annualMonthlyPrice']}
            refreshTrigger={refreshKey}
            externalSearchQuery={searchQuery}
            hideSearch
            onEdit={(record: any) => handleEdit(record)}
            onDelete={(record: any) => handleDelete(record)}
            customActions={customActions}
            customDataProcessor={(data: any[]) =>
                data.map(item => ({
                    ...item,
                    monthlyPrice: item.pricing?.monthlyPrice ?? 0,
                    annualMonthlyPrice: item.pricing?.annualMonthlyPrice ?? 0,
                }))
            }
            columnRenderers={{
                country: (row: any) => (
                    <Chip label={`${row.country} - ${countryLabelMap[row.country] || row.country}`} size="small" />
                ),
                monthlyPrice: (row: any) => formatPrice(row.pricing?.monthlyPrice || 0, row.country),
                annualMonthlyPrice: (row: any) => formatPrice(row.pricing?.annualMonthlyPrice || 0, row.country),
            }}
        />
    );

    const toolbarBgSx = {
        backgroundColor: (t: any) =>
            t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
    };

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: '/administration/business-plans', itemLabel: 'Business Plans' },
                ]}
            />

            {/* Toolbar */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: { xs: 1, md: 2 },
                    alignItems: 'center',
                    mb: 3,
                    p: 1.5,
                    borderRadius: 2,
                    ...toolbarBgSx,
                }}
            >
                {/* Mobile: Search toggle */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    <IconButton
                        color="secondary"
                        onClick={() => setMobileSearchOpen(prev => !prev)}
                        size="large"
                        sx={{
                            border: '1px solid',
                            borderColor: 'secondary.main',
                            '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
                        }}
                    >
                        <Search />
                    </IconButton>
                </Box>

                {/* Desktop: Search Box */}
                <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1, maxWidth: 260 }}>
                    <TextField
                        label="Search Business Plans"
                        variant="outlined"
                        color="secondary"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        fullWidth
                    />
                </Box>

                {/* Desktop: Filters */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, flexWrap: 'wrap' }}>
                    <Autocomplete
                        options={envConfig?.countries || []}
                        getOptionLabel={(opt) => `${opt.code} - ${opt.label}`}
                        value={envConfig?.countries.find(c => c.code === filterCountry) || null}
                        onChange={(_e, val) => setFilterCountry(val?.code || '')}
                        renderInput={(params) => (
                            <TextField {...params} label="Country" size="small" sx={{ minWidth: 180 }} />
                        )}
                        isOptionEqualToValue={(opt, val) => opt.code === val.code}
                        size="small"
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Domain</InputLabel>
                        <Select
                            value={filterDomain}
                            label="Domain"
                            onChange={(e) => setFilterDomain(e.target.value as string)}
                        >
                            <MenuItem value="">All Domains</MenuItem>
                            {availableDomains.map(d => (
                                <MenuItem key={d} value={d}>{d}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Spacer for mobile */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1 }} />

                {/* Desktop: View Mode Toggle + Create */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_e, newMode) => {
                            if (newMode !== null) {
                                setViewMode(newMode);
                                localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                            }
                        }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                border: '1px solid',
                                borderColor: 'divider',
                                '&.Mui-selected': {
                                    backgroundColor: (t) => t.palette.mode === 'dark' ? '#191f45' : 'secondary.main',
                                    color: (t) => t.palette.mode === 'dark' ? '#fff' : 'secondary.contrastText',
                                    '&:hover': { backgroundColor: (t) => t.palette.mode === 'dark' ? '#232a54' : 'secondary.dark' },
                                },
                            },
                        }}
                    >
                        <ToggleButton value="tile" aria-label="Tile view">
                            <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="Table view">
                            <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Add />}
                        onClick={handleCreate}
                        size="small"
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Create Business Plan
                    </Button>
                </Box>

                {/* Mobile: Create icon button */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    <IconButton
                        color="secondary"
                        onClick={handleCreate}
                        size="large"
                        sx={{
                            border: '1px solid',
                            borderColor: 'secondary.main',
                            '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
                        }}
                    >
                        <Add />
                    </IconButton>
                </Box>
            </Box>

            {/* Mobile: Expandable search + filters */}
            {mobileSearchOpen && (
                <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
                    <TextField
                        label="Search Business Plans"
                        variant="outlined"
                        color="secondary"
                        size="small"
                        fullWidth
                        autoFocus
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Autocomplete
                            options={envConfig?.countries || []}
                            getOptionLabel={(opt) => `${opt.code} - ${opt.label}`}
                            value={envConfig?.countries.find(c => c.code === filterCountry) || null}
                            onChange={(_e, val) => setFilterCountry(val?.code || '')}
                            renderInput={(params) => (
                                <TextField {...params} label="Country" size="small" />
                            )}
                            isOptionEqualToValue={(opt, val) => opt.code === val.code}
                            size="small"
                            sx={{ flex: 1 }}
                        />
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Domain</InputLabel>
                            <Select
                                value={filterDomain}
                                label="Domain"
                                onChange={(e) => setFilterDomain(e.target.value as string)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {availableDomains.map(d => (
                                    <MenuItem key={d} value={d}>{d}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            )}

            {/* Content */}
            {loading && plans.length === 0 ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress color="secondary" />
                </Box>
            ) : viewMode === 'tile' ? (
                renderTileView()
            ) : (
                renderTableView()
            )}

            {/* Create/Edit Dialog */}
            {dialogOpen && envConfig && (
                <BusinessPlanDialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    onSave={handleDialogSave}
                    plan={editingPlan}
                    envConfig={envConfig}
                    allPlans={plans}
                />
            )}

            {/* View Dialog */}
            <BusinessPlanViewDialog
                open={viewDialogOpen}
                onClose={() => { setViewDialogOpen(false); setViewingPlan(null); }}
                plan={viewingPlan}
                envConfig={envConfig}
            />
        </Box>
    );
};

export default BusinessPlans;
